import {
  Box,
  Button,
  DialogActionTrigger,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  HStack,
  IconButton,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';

import { CenteredDialogContent } from '#components/ui/centered-dialog-content';
import { toaster } from '#components/ui/toaster';

export interface AdvancedDocumentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentId?: string;
  documentFormat: string | null;
  canConvertToMarkdown?: boolean;
  onConvertToMarkdown?: () => void | Promise<void>;
  canConvertToGrid?: boolean;
  onConvertToGrid?: () => void | Promise<void>;
  getCurrentValue: () => string;
}

export function AdvancedDocumentModal({
  open,
  onOpenChange,
  documentId,
  documentFormat,
  canConvertToMarkdown = false,
  onConvertToMarkdown,
  canConvertToGrid = false,
  onConvertToGrid,
  getCurrentValue,
}: AdvancedDocumentModalProps) {
  const getCurrentValueRef = useRef(getCurrentValue);
  const [rawValueSnapshot, setRawValueSnapshot] = useState<string>('');

  useEffect(() => {
    getCurrentValueRef.current = getCurrentValue;
  }, [getCurrentValue]);

  useEffect(() => {
    if (!open) return;

    try {
      setRawValueSnapshot(getCurrentValueRef.current());
    } catch {
      setRawValueSnapshot('');
    }
  }, [documentFormat, open]);

  return (
    <DialogRoot open={open} onOpenChange={(e) => onOpenChange(e.open)}>
      <CenteredDialogContent>
        <DialogHeader>
          <DialogTitle>Advanced</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack gap={5} align="stretch">
            <Box>
              <HStack gap={2} align="flex-start" justify="space-between" mb={2}>
                <Text fontSize="sm" color="fg.muted">
                  Format
                </Text>
                <HStack gap={2} flexWrap="wrap" justify="flex-end">
                  {canConvertToMarkdown && onConvertToMarkdown ? (
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() => void onConvertToMarkdown()}
                    >
                      Convert to markdown
                    </Button>
                  ) : null}
                  {canConvertToGrid && onConvertToGrid ? (
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() => void onConvertToGrid()}
                    >
                      Convert to Grid
                    </Button>
                  ) : null}
                </HStack>
              </HStack>
              <Text fontFamily="mono" fontSize="sm" color="fg">
                {documentFormat ?? '—'}
              </Text>
            </Box>

            <Box>
              <Text fontSize="sm" color="fg.muted" mb={2}>
                Document ID
              </Text>
              <HStack gap={2} align="flex-start">
                <Text
                  fontFamily="mono"
                  fontSize="sm"
                  color="fg"
                  wordBreak="break-all"
                  flex={1}
                >
                  {documentId ?? '—'}
                </Text>
                {documentId && (
                  <IconButton
                    size="sm"
                    variant="ghost"
                    aria-label="Copy document ID"
                    title="Copy to clipboard"
                    onClick={() => {
                      void navigator.clipboard
                        .writeText(documentId)
                        .then(() => {
                          toaster.create({
                            title: 'Copied',
                            description: 'Document ID copied to clipboard',
                            type: 'success',
                          });
                        })
                        .catch(() => {
                          toaster.create({
                            title: 'Copy failed',
                            description: 'Could not copy to clipboard',
                            type: 'error',
                          });
                        });
                    }}
                  >
                    📋
                  </IconButton>
                )}
              </HStack>
            </Box>

            <Box>
              <HStack gap={2} align="flex-start" justify="space-between" mb={2}>
                <Text fontSize="sm" color="fg.muted">
                  Raw value
                </Text>
                <IconButton
                  size="sm"
                  variant="ghost"
                  aria-label="Copy raw value"
                  title="Copy raw value to clipboard"
                  disabled={!rawValueSnapshot}
                  onClick={() => {
                    void navigator.clipboard
                      .writeText(rawValueSnapshot)
                      .then(() => {
                        toaster.create({
                          title: 'Copied',
                          description: 'Raw value copied to clipboard',
                          type: 'success',
                        });
                      })
                      .catch(() => {
                        toaster.create({
                          title: 'Copy failed',
                          description: 'Could not copy to clipboard',
                          type: 'error',
                        });
                      });
                  }}
                >
                  📋
                </IconButton>
              </HStack>

              <Box
                borderWidth="1px"
                borderRadius="md"
                borderColor="border"
                overflowY="auto"
                maxH="280px"
                p={2}
              >
                <Text
                  as="pre"
                  fontFamily="mono"
                  fontSize="xs"
                  color="fg"
                  whiteSpace="pre-wrap"
                  wordBreak="break-word"
                  m={0}
                >
                  {rawValueSnapshot || '—'}
                </Text>
              </Box>
            </Box>
          </VStack>
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button variant="outline">Close</Button>
          </DialogActionTrigger>
        </DialogFooter>
      </CenteredDialogContent>
    </DialogRoot>
  );
}
